import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ApplicationState } from '../../../store/application-store';
import { Observable, combineLatest } from 'rxjs';
import { takeWhile, map, take } from 'rxjs';
import { DiaryEventGroup } from '../../models/diary-event-group';
import { ActivatedRoute } from '@angular/router';
import { RouteContextService } from '../../../context/services/route-context.service';
import * as diarySelectors from '../../selectors/diary-selectors';
import * as diaryActions from '../../actions/diary-actions';
import { IContext } from './../../../context/models/context';
import { SelectedDateRange } from './../../models/selected-date-range';
import { DiaryCalendarView } from '../../models/diary-calendar-view';
import { WidgetComponent } from '../../../dashboards/components/widget-component/widget-component';
import { RuleSet, Rule } from 'ngx-angular-query-builder';
import { QueryBuilderConfigProvider } from '../../../common/filter/query-builder-config-provider';
import { DiaryEventFilterFieldProvider } from '../../filter/diary-event-filter-field-provider';
import * as WidgetConfigSelectors from '../../../widget-configs/selectors/widget-configs';
import { FilterEditorDialogResult } from '../../../common/filter/models/filter-editor-dialog-result';
import * as filterActions from '../../../common/filter/actions/filter.actions';
import * as filterUtils from '../../../common/filter/filter-utils';
import { ConfirmationService } from 'primeng/api';
import { getEffectiveProduct } from '../../../user-settings/selectors/user-settings';
import { DataFormatter } from '@common/services/data-formatter.service';
import { ActionDispatcher } from '@feature/store/dispatcher.service';
import * as widgetConfigActions from "@dashboard-widget/actions/dashboard-widget-config.actions";
import { DiaryConfiguration } from '@diary/models/diary-configuration';
import { DiaryWidgetFacadeService } from '@diary/services/diary-event-facade-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mojo-diary-widget',
  templateUrl: './diary-widget.component.html'
})
export class DiaryWidgetComponent extends WidgetComponent implements OnInit, OnDestroy {

  diaryEvents$: Observable<DiaryEventGroup[]>;
  diaryEventsForEventCards$: Observable<DiaryEventGroup[]>;
  diaryEventsLoading$: Observable<boolean>;
  noticeOptions: Observable<object[]>;
  isAlive: boolean = true;
  context: IContext;
  date: Date = new Date();
  view: DiaryCalendarView = DiaryCalendarView.Month;
  eventFilterText: string = 'Showing all events';
  sysAdminFilterText$: Observable<string[]>;
  currentFilter: RuleSet;
  selectedDate: Date = new Date();
  searchString: string = "";
  selectedDateRange: any = {}
  includeNotices: boolean;
  buttons: string[] = ["Dated", "Undated"]
  selectedFilterButton: string = this.buttons[0];

  configProvider: QueryBuilderConfigProvider;
  private fieldprovider: DiaryEventFilterFieldProvider;


  constructor(
    protected store: Store<ApplicationState>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly contextService: RouteContextService,
    protected confirmationService: ConfirmationService,
    private readonly dataFormatter: DataFormatter,
    private readonly dispatcher: ActionDispatcher,
    private readonly diaryWidgetFacadeService: DiaryWidgetFacadeService
  ) {
    super(store, confirmationService);
  }

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  ngOnInit() {
    super.ngOnInit();

    this.context = this.contextService.getContextFromRoute(this.activatedRoute);

    this.diaryEventsFilter();

    this.dispatcher.dispatch(
          widgetConfigActions.getDashboardWidgetConfiguration({
            widgetId: this.widget.id,
          }),
        );

        this.noticeOptions = this.diaryWidgetFacadeService.getDiaryNoticeOptions(this.widget);

    const sysAdminFilters = WidgetConfigSelectors.getSysAdminFilters(this.widget.widgetConfigId);

    const adminFilter = this.store.pipe(select(sysAdminFilters));
    const effectiveProduct = this.store.pipe(select(getEffectiveProduct));
    this.configProvider = new QueryBuilderConfigProvider(null);

    combineLatest(adminFilter, effectiveProduct)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(([adminFilterRules, product]) => {
        this.fieldprovider = new DiaryEventFilterFieldProvider(this.widget.dashboardName, adminFilterRules, product);
        this.configProvider.setFilterFieldProvider(this.fieldprovider);
      });

    this.diaryEventsLoading$ = this.store.pipe(select(diarySelectors.getDiaryEventsLoading(this.widget.id.toString())));
    this.diaryEvents$ = this.diaryWidgetFacadeService.getEvents(this.view, this.widget, this.fieldprovider);

    this.diaryEventsForEventCards$ = this.diaryEvents$.pipe(
      map((diaryEventGroups: DiaryEventGroup[]) => {
        if (this.view === DiaryCalendarView.Day) {
          return diaryEventGroups.filter((diaryEventGroup: DiaryEventGroup) => {
            const eventDate = diaryEventGroup.DiaryEvents[0].DiaryDate.getDate();
            return eventDate === this.selectedDate.getDate();
          });
        }
        return diaryEventGroups;

      }));
    this.eventFilterText = this.getFilterString(this.currentFilter);
    this.sysAdminFilterText$ = this.store.pipe(select(WidgetConfigSelectors.getSysAdminFilterStrings(this.widget.widgetConfigId)));
    this.store.dispatch(new filterActions.LoadWidgetFilter(new filterActions.WidgetFilterActionPayload(this.widget.id)));


    this.store
      .pipe(
        select(diarySelectors.getFilter(this.widget.id.toString())),
        takeWhile(() => this.isAlive)
      ).subscribe((filter) => {
        this.currentFilter = filter;
        this.eventFilterText = this.getFilterString(this.currentFilter);
      });

  }

  export() {
    this.diaryWidgetFacadeService.export(this.view, this.widget, this.fieldprovider, this.view === DiaryCalendarView.Day ? this.selectedDate : null);
  }

  diaryEventsFilter () {
    this.store.dispatch(new diaryActions.Filter(this.widget.id.toString(), this.searchString));
  }
  dateChanged(selectedDateRange: SelectedDateRange) {
    this.selectedDate = selectedDateRange.SelectedDate;
    this.selectedDateRange = selectedDateRange;

    this.diaryWidgetFacadeService.getDashboardWidgetConfig(this.widget).pipe(take(1)).subscribe((dashboardWidgetConfig) => {
      this.includeNotices = dashboardWidgetConfig?.IncludeNotices ?? null;
      this.store.dispatch(new diaryActions.Load(this.context, this.selectedDateRange.StartDate, this.selectedDateRange.EndDate, this.widget.id));
    })

  }

  getFilterString(filter: RuleSet): string {
    if (filter && filter.rules && filter.rules.length > 0) {
      let resultString: string = '';
      filter.rules.forEach(x => {
        if (!(x as RuleSet).rules) {
          const rule = x as Rule;
          let condition: string = '';
          if (resultString.length > 0) {
            condition = `${filter.condition} `;
          } else {
            resultString = 'Showing events for ';
          }
          let operatorString = rule.operator;
          if (rule.operator === 'equal') {
            operatorString = 'is';
          }
          const item: string = `${condition}${rule.field} ${operatorString} ${this.getRuleValue(rule)}`;
          resultString = resultString + ' ' + item;
        }
      });

      return resultString;
    }
    return 'Showing all events';
  }

  filterEdited(result: FilterEditorDialogResult): void {
    this.saveDashboardWidgetConfig();
    this.store.dispatch(new filterActions.UpdateWidgetFilter(new filterActions.WidgetFilterActionPayload(this.widget.id, result.filter)));
  }

   saveDashboardWidgetConfig() {
      const cfg: DiaryConfiguration = {
        IncludeNotices: this.includeNotices
      };

      this.dispatcher.dispatch(
        widgetConfigActions.saveDashboardWidgetConfiguration({
          widgetId: this.widget.id,
          config: cfg,
        }),
      );
    }

  getRuleValue(rule): string {
    if (rule.operator.toLowerCase() === 'between') {
      if (this.isDate(rule.value.field1.toString())) {
        return `${this.dataFormatter.formatDate(rule.value.field1)} and
                ${this.dataFormatter.formatDate(rule.value.field2)}`;
      } else {
        return `${rule.value.field1} and ${rule.value.field2}`;
      }
    } else if (this.isDate(rule.value.toString())) {
      return this.dataFormatter.formatDate(rule.value);
    }
    return rule.value;
  }

  isDate(value: string): boolean {
    return isNaN(+value) && !isNaN(Date.parse(value));
  }

  isButtonSelected(btn: string) {
    return this.selectedFilterButton === btn;
  }

  handleButtonClick(btn: string) {
    if(btn == 'Undated'){
      this.store.dispatch(new diaryActions.LoadUndatedEvents(this.context, this.widget.id.toString()));
    }
    else{
      this.store.dispatch(new diaryActions.Load(this.context, this.selectedDateRange.StartDate, this.selectedDateRange.EndDate, this.widget.id));
    }
    this.selectedFilterButton = btn;
  }
}
